import React, { ButtonHTMLAttributes } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  fullWidth = false,
  children,
  className = '',
  ...props
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 shadow-card';
      case 'secondary':
        return 'bg-surface-overlay/85 text-content hover:bg-surface-overlay focus:ring-secondary-500 border border-border backdrop-blur-xl';
      case 'outline':
        return 'border border-border bg-surface-raised/50 text-content hover:bg-surface-overlay/90 focus:ring-primary-500 backdrop-blur-xl';
      case 'text':
        return 'text-primary-600 hover:text-primary-700 hover:bg-primary-50';
      case 'ghost':
        return 'text-content-secondary hover:bg-surface-overlay/80 hover:text-content';
      default:
        return 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 shadow-card';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3.5 py-2 text-sm rounded-full';
      case 'md':
        return 'px-4.5 py-2.5 text-sm rounded-full';
      case 'lg':
        return 'px-6 py-3 text-base rounded-full';
      default:
        return 'px-4.5 py-2.5 text-sm rounded-full';
    }
  };

  return (
    <button
      className={`
        inline-flex items-center justify-center rounded-md font-medium
        transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${fullWidth ? 'w-full' : ''}
        ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

export default Button;